import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Amplify } from 'aws-amplify'
import { signInWithRedirect } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import {
  Authenticator,
  useAuthenticator,
  ThemeProvider as AmplifyThemeProvider,
} from '@aws-amplify/ui-react'
import '@aws-amplify/ui-react/styles.css'
import { useRollbar } from '@rollbar/react'

// Amplify Overrides
import 'ui/screens/SignIn/AmplifyCSSOverrides.css'
import { amplifyThemeOverrides } from 'ui/screens/SignIn/AmplifyThemeOverrides'
import { formFields } from 'ui/screens/SignIn/AmplifyFormFieldOverrides'
import { configureAmplifyI18n } from 'ui/screens/SignIn/AmplifyI18nOverrides'

// Components
import { Stack } from 'ui/baseComponents'
import { AmplifyComponentCustomizations } from 'ui/screens/SignIn/AmplifyComponentCustomizations'

// Constants
import {
  COGNITO_ATTR_DEFAULT_LOGIN_PORTAL,
  DEFAULT_LOGIN_PORTAL_STRIVE_STUDY,
  DEFAULT_LOGIN_PORTAL_STRIVE_STUDY_REQUIRED,
  PASSWORD_POLICY,
  STRIVE_STUDY_URL,
} from 'ui/screens/SignIn/consts'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'

// Helpers
import {
  getUserAttributes,
  handleForgotPassword,
  handleForgotPasswordSubmit,
  handleSignIn,
} from 'ui/screens/SignIn/helpers'

// LaunchDarkly
import { useFlags } from 'domains/launchdarkly/hooks'

// Zustand
import useBoundStore from 'domains/zustand/store'

const awsConfig = window.Rune.Carrot.config.vendors.aws

const EMAIL_PASSWORD_SIGNIN_CONFIG = {
  Auth: {
    Cognito: {
      userPoolClientId: awsConfig.cognito.clientId,
      userPoolId: awsConfig.cognito.userPoolId,
    },
  },
}

const SSO_CONFIG = {
  Auth: {
    Cognito: awsConfig.cognitoSSO,
  },
}

const SSO_PROVIDER = 'Duo'
const AUTH_METHOD_KEY = 'authMethod'
const AUTH_METHODS = {
  EMAIL_PASSWORD: 'EMAIL_PASSWORD',
  SSO: 'SSO',
}

const configureAmplify = () => {
  const urlParams = new URLSearchParams(window.location.search)
  // Check for stored SSO preference first, then check URL params
  const storedAuthMethod = sessionStorage.getItem(AUTH_METHOD_KEY)

  if (urlParams.has('code') || storedAuthMethod === AUTH_METHODS.SSO) {
    Amplify.configure(SSO_CONFIG)
    // Store that we're using SSO for future page loads
    sessionStorage.setItem(AUTH_METHOD_KEY, AUTH_METHODS.SSO)
  } else {
    Amplify.configure(EMAIL_PASSWORD_SIGNIN_CONFIG)
    // Only set to email/password if not already set to SSO
    if (storedAuthMethod !== AUTH_METHODS.SSO) {
      sessionStorage.setItem(AUTH_METHOD_KEY, AUTH_METHODS.EMAIL_PASSWORD)
    }
  }
}

configureAmplify()
configureAmplifyI18n()

const SignIn = () => {
  const user = useBoundStore((state) => state.user)
  const setUser = useBoundStore((state) => state.setUser)
  const { duoSsoEnabled } = useFlags()
  const [isDuoLogin, setIsDuoLogin] = useState(false) // eslint-disable-line no-unused-vars
  const [multipleIdentities, setMultipleIdentities] = useState(false)

  const navigate = useNavigate()

  const { authStatus, user: authUser } = useAuthenticator((context) => [
    context.authStatus,
    context.user,
  ])

  const rollbar = useRollbar()

  const loginWithDuo = async () => {
    setIsDuoLogin(true)
    Amplify.configure(SSO_CONFIG)

    const referrer = sessionStorage.getItem('referrer')
    if (referrer) {
      localStorage.setItem('sso_referrer', referrer)
    }

    await signInWithRedirect({
      customState: SSO_PROVIDER,
      provider: {
        custom: SSO_PROVIDER,
      },
    })
  }

  useEffect(() => {
    Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'customOAuthState') {
        if (payload.data === SSO_PROVIDER) {
          setIsDuoLogin(true)
        }
      }
    })
  }, [])

  /* Set the user state in Zustand once authenticated
   * and route the user to the appropriate portal
   */
  useEffect(() => {
    const configureRollbar = (user) => {
      rollbar.configure({
        payload: {
          person: user,
        },
      })
    }

    const routeAuthenticatedUser = (user) => {
      const referrer = sessionStorage.getItem('referrer')
      const ssoReferrer = localStorage.getItem('sso_referrer')
      const isSSO = sessionStorage.getItem(AUTH_METHOD_KEY) === AUTH_METHODS.SSO
      const portal = user[COGNITO_ATTR_DEFAULT_LOGIN_PORTAL]
      const sendToStriveStudy =
        portal === DEFAULT_LOGIN_PORTAL_STRIVE_STUDY ||
        portal === DEFAULT_LOGIN_PORTAL_STRIVE_STUDY_REQUIRED

      if (sendToStriveStudy) {
        window.location.href = STRIVE_STUDY_URL
        return
      }

      let goTo = ROUTE_METADATA.patients.path
      if (referrer) {
        goTo = referrer
      } else if (ssoReferrer && isSSO) {
        goTo = ssoReferrer
      }

      sessionStorage.removeItem('referrer')
      localStorage.removeItem('sso_referrer')
      navigate(goTo)
    }

    if (authStatus === 'authenticated' && authUser) {
      if (!user) {
        getUserAttributes().then((attributes) => {
          /* When logging in with Duo SSO, an 'identities' attribute is returned.
           *  We parse it to get the user's email.
           *  We mainly care about this for Rollbar configuration.
           */
          let identity
          if (attributes.identities) {
            const parsedIdentities = JSON.parse(attributes.identities)

            /* There should only be one identity returned from Cognito.
             *  If there are more, we log an error and update to value of multipleIdentities to display an alert.
             */
            if (parsedIdentities.length > 1) {
              console.error(
                'More than one identity returned from Cognito. Expected only one.',
              )
              setMultipleIdentities(true)
            }
            ;[identity] = parsedIdentities
            identity.email = identity.userId
          }

          const userAttributes = identity
            ? { ...attributes, ...identity }
            : attributes

          const userConfiguration = {
            id: authUser.username,
            ...userAttributes,
          }

          setUser(userConfiguration)
          configureRollbar(userConfiguration)
          routeAuthenticatedUser(userConfiguration)
        })
      } else if (window.location.pathname === '/signin') {
        routeAuthenticatedUser(user)
      }
    }
  }, [authStatus, JSON.stringify(authUser), user, rollbar]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack
      direction="column"
      sx={{ height: '100vh' }}
      justifyContent="center"
      alignItems="center"
    >
      <AmplifyThemeProvider theme={amplifyThemeOverrides}>
        <Authenticator
          formFields={formFields}
          hideSignUp
          passwordSettings={PASSWORD_POLICY}
          components={AmplifyComponentCustomizations({
            duoSsoEnabled,
            loginWithDuo,
            multipleIdentities,
          })}
          services={{
            handleForgotPassword,
            handleForgotPasswordSubmit,
            handleSignIn,
          }}
        ></Authenticator>
      </AmplifyThemeProvider>
    </Stack>
  )
}

export default SignIn
